/**
 * File-format detection for preview shares — one place both the PM
 * dialog and the public client page (/p/*) use, so the format badge
 * ("ProRes", "MP4", "PDF", …) and its color are always identical.
 *
 * NOTE: MOV is treated as ProRes by convention here (the studio's MOV
 * deliveries are ProRes masters) and is therefore NOT browser-playable —
 * such files render as clear direct downloads on the client page.
 */

export type FileFormat = {
  /** Short badge label, e.g. "ProRes", "MP4", "PDF". */
  label: string;
  /** Badge text color (on a translucent tinted background). */
  color: string;
  /** Whether the browser can preview this format inline. */
  previewable: boolean;
};

const FMT = (label: string, color: string, previewable: boolean): FileFormat =>
  ({ label, color, previewable });

/** Keyed by lowercase extension. Colors are grouped per family. */
const EXT_FORMATS: Record<string, FileFormat> = {
  // Video — playable
  mp4: FMT('MP4', '#4da3ff', true),
  m4v: FMT('MP4', '#4da3ff', true),
  webm: FMT('WebM', '#4da3ff', true),
  // Video — masters / download-only
  mov: FMT('ProRes', '#c084fc', false),
  mxf: FMT('MXF', '#c084fc', false),
  avi: FMT('AVI', '#c084fc', false),
  // Images
  jpg: FMT('JPG', '#34d399', true),
  jpeg: FMT('JPG', '#34d399', true),
  png: FMT('PNG', '#34d399', true),
  gif: FMT('GIF', '#34d399', true),
  webp: FMT('WebP', '#34d399', true),
  avif: FMT('AVIF', '#34d399', true),
  // Working files (download-only; PSD may get a server-side preview)
  psd: FMT('PSD', '#38bdf8', false),
  psb: FMT('PSB', '#38bdf8', false),
  ai: FMT('AI', '#fb923c', false),
  eps: FMT('EPS', '#fb923c', false),
  tif: FMT('TIFF', '#38bdf8', false),
  tiff: FMT('TIFF', '#38bdf8', false),
  exr: FMT('EXR', '#38bdf8', false),
  // Documents
  pdf: FMT('PDF', '#f87171', false),
  // Audio
  mp3: FMT('MP3', '#fbbf24', true),
  wav: FMT('WAV', '#fbbf24', true),
  aac: FMT('AAC', '#fbbf24', true),
  ogg: FMT('OGG', '#fbbf24', true),
  flac: FMT('FLAC', '#fbbf24', true),
  m4a: FMT('M4A', '#fbbf24', true),
  // Archives
  zip: FMT('ZIP', '#9ca3af', false),
  rar: FMT('RAR', '#9ca3af', false),
  '7z': FMT('7Z', '#9ca3af', false),
};

export function fileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() || '';
}

/**
 * TRUE when the extension is a KNOWN format that browsers cannot preview
 * (ProRes/MOV, PSD, PDF, ZIP, …). Unknown extensions return false so
 * callers can still fall back to MIME-based detection.
 */
export function isDownloadOnlyFormat(fileName: string): boolean {
  const f = EXT_FORMATS[fileExtension(fileName)];
  return !!f && !f.previewable;
}

const VIDEO_EXTS = new Set(['mp4', 'm4v', 'webm', 'mov', 'mxf', 'avi']);

export function isVideoFile(fileName: string): boolean {
  return VIDEO_EXTS.has(fileExtension(fileName));
}

/** Best-effort media metadata attached to a share file. */
export type MediaMeta = {
  fileName: string;
  durationMs?: number | null;
  mediaWidth?: number | null;
  mediaHeight?: number | null;
};

/** Duration tolerance: encoders differ by a frame or two between exports. */
const DURATION_TOLERANCE_MS = 500;
/** Aspect-ratio tolerance (1%) — covers rounding in encoded dimensions. */
const ASPECT_TOLERANCE = 0.01;

/**
 * TRUE only when we can be CONFIDENT two grouped files are the same
 * content in different formats:
 * - videos: both durations known and equal (±0.5s); if both dimensions
 *   are known, aspect ratios must match too. Missing duration on either
 *   side → NOT verified (never guess).
 * - non-videos: aspect ratios must match when both dimensions are known;
 *   without metadata we trust the PM's manual grouping.
 */
export function mediaMatches(a: MediaMeta, b: MediaMeta): boolean {
  const aspectOk = () => {
    if (a.mediaWidth && a.mediaHeight && b.mediaWidth && b.mediaHeight) {
      const ra = a.mediaWidth / a.mediaHeight;
      const rb = b.mediaWidth / b.mediaHeight;
      return Math.abs(ra - rb) / rb <= ASPECT_TOLERANCE;
    }
    return true; // unknown dimensions — no contradiction
  };
  if (isVideoFile(a.fileName) || isVideoFile(b.fileName)) {
    if (typeof a.durationMs !== 'number' || typeof b.durationMs !== 'number') return false;
    if (Math.abs(a.durationMs - b.durationMs) > DURATION_TOLERANCE_MS) return false;
    return aspectOk();
  }
  return aspectOk();
}

/** Format badge for a file; falls back to the uppercased extension. */
export function detectFileFormat(fileName: string): FileFormat {
  const ext = fileExtension(fileName);
  return EXT_FORMATS[ext] || FMT(ext ? ext.toUpperCase().slice(0, 6) : 'DATEI', '#9ca3af', false);
}
